import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Settings, Save } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Brisbane',
  'UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Asia/Singapore', 'Asia/Tokyo',
];
const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'SGD', 'NZD'];

export default function BackOfficeConfigGeneral() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/config');
      setConfig(res.data?.data || {});
    } catch { toast.error('Failed to load config'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/backoffice/config', config);
      toast.success('Configuration saved');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const update = (key, val) => setConfig((prev) => ({ ...prev, [key]: val }));

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><Settings className="w-7 h-7 text-[#0B6445]" />General Settings</h1>
          <p className="text-[#0E1B16]/70 mt-2">Core platform settings and preferences.</p>
        </div>

        {loading || !config ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : (
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">Platform Configuration</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-5 max-w-lg">
                <div className="space-y-1.5">
                  <Label>Platform Name</Label>
                  <Input value={config.platform_name || ''} onChange={(e) => update('platform_name', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Support Email</Label>
                  <Input type="email" value={config.support_email || ''} onChange={(e) => update('support_email', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Timezone</Label>
                  <Select value={config.timezone || 'UTC'} onValueChange={(v) => update('timezone', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Default Currency</Label>
                  <Select value={config.default_currency || 'AUD'} onValueChange={(v) => update('default_currency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="mb-0">KYC Auto-Approve</Label>
                  <button
                    type="button"
                    onClick={() => update('kyc_auto_approve', !config.kyc_auto_approve)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.kyc_auto_approve ? 'bg-[#0B6445]' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.kyc_auto_approve ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-xs text-slate-500">{config.kyc_auto_approve ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="mb-0">Maintenance Mode</Label>
                  <button
                    type="button"
                    onClick={() => update('maintenance_mode', !config.maintenance_mode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.maintenance_mode ? 'bg-red-500' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.maintenance_mode ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-xs text-slate-500">{config.maintenance_mode ? 'Active — platform in maintenance' : 'Off'}</span>
                </div>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">
                  <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </BackOfficeLayout>
  );
}
